import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "hooks/useApi";
import { isEmpty } from "lodash-es";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "./useNotification";

export const useFetchPersons = ({
  entityId,
  searchText,
  offsetDc,
  offsetDb,
  limit,
  enabled,
}: {
  entityId: string;
  searchText: string;
  offsetDc: number;
  offsetDb: number;
  limit: number;
  enabled: boolean;
}) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  const url = useMemo(() => {
    const params = new URLSearchParams();
    params.append("offsetDc", offsetDc.toString());
    params.append("offsetDb", offsetDb.toString());
    params.append("limit", limit.toString());
    if (!isEmpty(searchText)) {
      params.append("nameFullLocalContains", searchText);
    }
    return `/${sport}/o/${organizationId}/entities/${entityId}/persons?${params.toString()}`;
  }, [sport, organizationId, entityId, searchText, offsetDc, offsetDb, limit]);

  return useQuery({
    queryKey: ["persons", sport, organizationId, entityId, searchText, offsetDc, offsetDb, limit],
    queryFn: () => callApi(url, { defaultErrorMessage: "Can't fetch persons" }),
    enabled,
  });
};

export const useCreatePerson = () => {
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ data }: { data: any }) => {
      if (data?.dateOfBirth) {
        data.dateOfBirth = data.dateOfBirth.format("YYYY-MM-DD");
      }
      return callApi<any>(`/persons`, {
        method: "POST",
        body: {
          sport: sport,
          organizationId: organizationId,
          ...data,
        },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["persons", sport, organizationId],
      });
      showSuccessNotification(formatMessage({ id: "notification.person.created" }));
    },
  });
};
